// src/pages/WeeklyPlanPage.tsx
//
// Planning cockpit for management.
// Three views:
//   "Today"     — operational standup: focus, changes, help needed
//   "This Week" — strategic: goals, risk signals, due tasks, team load
//   "Review"    — end-of-week recap: completed, slipped, gap causes, load, next-week risks
//
// Reuses all existing data sources — no new backend endpoints.
// Works without Freedom framework.

import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useGetGoalsQuery } from '../api/goalsApi';
import { useGetTasksQuery, useUpdateTaskMutation } from '../api/tasksApi';
import {
  useGetManagementSummaryQuery,
  useGetTeamRebalancingRecommendationsQuery,
} from '../api/managementApi';
import { useCreateConversationMutation, useAddMessageMutation } from '../api/conversationsApi';
import { deriveInterventions, type Intervention, type InterventionType } from '../services/interventionEngine';
import { getInterventionCoverage, suppressCovered } from '../services/insightPrioritizer';
import LoadingSpinner from '../components/common/LoadingSpinner';
import type {
  GoalTrackingStatus,
  Goal,
  Task,
  ManagementSummary,
  RebalancingRecommendation,
  RebalancingUser,
} from '../types/api';

// ============================================================
// Date utilities
// ============================================================

function getWeekRange() {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? -6 : 1 - day; // align to Monday
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fmt = (d: Date) =>
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return {
    start: monday.toISOString().split('T')[0],
    end: sunday.toISOString().split('T')[0],
    label: `${fmt(monday)} – ${fmt(sunday)}, ${sunday.getFullYear()}`,
  };
}

const TODAY = new Date().toISOString().split('T')[0];
const WEEK = getWeekRange();

// yesterday ISO timestamp (start of yesterday, local midnight → UTC)
function getYesterdayISO(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}
const YESTERDAY_ISO = getYesterdayISO();

// ============================================================
// Sub-components shared across all views
// ============================================================

const PRIORITY_DOT: Record<string, string> = {
  urgent: 'bg-red-500',
  high:   'bg-orange-400',
  medium: 'bg-yellow-400',
  low:    'bg-gray-300',
};

const TRACKING_BADGE: Record<GoalTrackingStatus, { label: string; cls: string }> = {
  on_track:  { label: 'On Track', cls: 'text-green-700 bg-green-100' },
  ahead:     { label: 'Ahead',    cls: 'text-blue-700  bg-blue-100'  },
  behind:    { label: 'Behind',   cls: 'text-red-700   bg-red-100'   },
  completed: { label: 'Done',     cls: 'text-gray-700  bg-gray-100'  },
  no_data:   { label: 'No data',  cls: 'text-gray-500  bg-gray-100'  },
};

function SectionHeader({
  title,
  count,
  linkTo,
  linkLabel,
}: {
  title: string;
  count?: number;
  linkTo?: string;
  linkLabel?: string;
}) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
        {title}
        {count != null && (
          <span className="ml-2 text-xs font-normal text-gray-400 normal-case">({count})</span>
        )}
      </h2>
      {linkTo && (
        <Link to={linkTo} className="text-xs text-blue-600 hover:underline">
          {linkLabel ?? 'View all'}
        </Link>
      )}
    </div>
  );
}

function TaskRow({ task, dimmed }: { task: Task; dimmed?: boolean }) {
  const isOverdue =
    task.status !== 'done' && !!task.dueDate && task.dueDate.split('T')[0] < TODAY;
  return (
    <div className={`flex items-start gap-2 py-2 border-b border-gray-50 last:border-0 ${dimmed ? 'opacity-60' : ''}`}>
      <span
        className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_DOT[task.priority] ?? 'bg-gray-300'}`}
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 leading-snug truncate">{task.title}</p>
        {task.dueDate && (
          <p className={`text-xs mt-0.5 ${isOverdue ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
            {isOverdue ? 'Overdue · ' : ''}
            {new Date(task.dueDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </p>
        )}
        {task.blockerReason && (
          <p className="text-xs text-amber-600 mt-0.5 truncate">⚠ {task.blockerReason}</p>
        )}
      </div>
      {task.assigneeUserId && (
        <span className="flex-shrink-0 text-xs text-gray-400" title={task.assigneeUserId}>
          {task.assigneeUserId.substring(0, 6)}
        </span>
      )}
      {task.goalId && (
        <Link
          to={`/tasks?goalId=${task.goalId}`}
          className="flex-shrink-0 text-xs text-blue-400 hover:text-blue-600"
          title="Linked to goal"
        >
          ◎
        </Link>
      )}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className="text-sm text-gray-400 py-4 text-center">{message}</p>;
}

// ============================================================
// Shared interfaces (used by component + meeting primer builders)
// ============================================================

interface HelpNeededEntry {
  userId: string;
  blockedCount: number;
  overdueCount: number;
  openTaskCount: number;
  estimatedMinutes: number;
}

interface ContribEntry {
  userId: string;
  completedThisWeek: number;
  openTaskCount: number;
  estimatedMinutes: number;
  blockedCount: number;
  overdueCount: number;
}

interface GapCauses {
  withBlocker: number;
  withReopen: number;
  withoutContext: number;
  unestimatedSlipped: number;
  blockerSamples: string[];
  total: number;
}

// ============================================================
// Meeting context primer builders
// ============================================================

function fmtTask(t: Task): string {
  const who    = t.assigneeUserId ? ` [@${t.assigneeUserId.substring(0, 8)}]` : '';
  const due    = t.dueDate ? ` · due ${t.dueDate.split('T')[0]}` : '';
  const blk    = t.blockerReason ? ` ⚠ ${t.blockerReason}` : '';
  return `- ${t.title}${who}${due}${blk}`;
}

function fmtList(items: string[], limit = 5): string {
  const shown = items.slice(0, limit);
  if (items.length > limit) shown.push(`  … and ${items.length - limit} more`);
  return shown.length ? shown.join('\n') : '  (none)';
}

function buildStandupPrimer(
  dueToday: Task[],
  overdueCarryover: Task[],
  activeBlocked: Task[],
  helpNeeded: HelpNeededEntry[],
  todayLabel: string,
): string {
  const lines: string[] = [];
  lines.push(`**Daily Standup — ${todayLabel}**`);
  lines.push('');
  lines.push(`DUE TODAY (${dueToday.length}):`);
  lines.push(fmtList(dueToday.map(fmtTask)));
  if (overdueCarryover.length > 0) {
    lines.push('');
    lines.push(`OVERDUE CARRYOVER (${overdueCarryover.length}):`);
    lines.push(fmtList(overdueCarryover.map(fmtTask)));
  }
  if (activeBlocked.length > 0) {
    lines.push('');
    lines.push(`ACTIVE BLOCKERS (${activeBlocked.length}):`);
    lines.push(fmtList(activeBlocked.map(fmtTask)));
  }
  if (helpNeeded.length > 0) {
    lines.push('');
    lines.push('HELP NEEDED:');
    for (const u of helpNeeded.slice(0, 4)) {
      const parts: string[] = [];
      if (u.blockedCount > 0) parts.push(`${u.blockedCount} blocked`);
      if (u.overdueCount > 0) parts.push(`${u.overdueCount} overdue`);
      lines.push(`- ${u.userId}: ${parts.join(', ')}`);
    }
  }
  lines.push('');
  lines.push('Please turn this into a concise standup agenda covering: what we are focusing on today, current blockers that need attention, and who needs help. Keep it brief and action-oriented for a 15-minute standup.');
  return lines.join('\n');
}

function buildWeeklyPlanPrimer(
  goals: Goal[],
  focusTasks: Task[],
  overdueTasks: Task[],
  dueThisWeek: Task[],
  mgmt: ManagementSummary | undefined,
  recs: RebalancingRecommendation[],
  overloaded: RebalancingUser[],
  behindGoalsCount: number,
  weekLabel: string,
): string {
  const lines: string[] = [];
  lines.push(`**Weekly Planning — ${weekLabel}**`);
  lines.push('');
  if (goals.length > 0) {
    lines.push(`ACTIVE GOALS (${goals.length}):`);
    for (const g of goals.slice(0, 6)) {
      const ts  = g.progress.trackingStatus.replace('_', ' ');
      const pct = g.progress.progressPercent != null ? ` ${g.progress.progressPercent.toFixed(0)}%` : '';
      const ovd = g.executionSummary?.overdueTasks ? ` · ${g.executionSummary.overdueTasks} overdue` : '';
      lines.push(`- ${g.title} [${ts}${pct}]${ovd}`);
    }
    lines.push('');
  }
  lines.push('RISK SIGNALS:');
  lines.push(`- ${behindGoalsCount} behind goals · ${mgmt?.gaps.overdueTasks ?? 0} overdue · ${mgmt?.gaps.blockedTasks ?? 0} blocked`);
  if (focusTasks.length > 0) {
    lines.push('');
    lines.push(`PRIORITY FOCUS (${focusTasks.length}):`);
    lines.push(fmtList(focusTasks.map(fmtTask)));
  }
  if (overdueTasks.length > 0) {
    lines.push('');
    lines.push(`OVERDUE CARRYOVER (${overdueTasks.length}):`);
    lines.push(fmtList(overdueTasks.map(fmtTask)));
  }
  if (dueThisWeek.length > 0) {
    lines.push('');
    lines.push(`DUE THIS WEEK (${dueThisWeek.length}):`);
    lines.push(fmtList(dueThisWeek.map(fmtTask)));
  }
  if (overloaded.length > 0) {
    lines.push('');
    lines.push('TEAM LOAD — OVERLOADED:');
    for (const u of overloaded.slice(0, 3)) {
      const h = u.estimatedMinutes > 0 ? ` ~${Math.round(u.estimatedMinutes / 60)}h` : '';
      lines.push(`- ${u.userId}: ${u.openTasks} tasks${h}`);
    }
  }
  if (recs.length > 0) {
    lines.push('');
    lines.push('REBALANCING SUGGESTIONS:');
    for (const r of recs.slice(0, 3)) {
      lines.push(`- "${r.taskTitle}": ${r.fromUserId.substring(0, 8)} → ${r.toUserId.substring(0, 8)} (${r.reason})`);
    }
  }
  lines.push('');
  lines.push('Please turn this into a weekly planning agenda with: key priorities and focus areas, goals that need attention, risk mitigation steps, rebalancing recommendations, and recommended weekly focus for the team.');
  return lines.join('\n');
}

function buildWeeklyReviewPrimer(
  completedThisWeek: Task[],
  slippedThisWeek: Task[],
  unresolvedOverdue: Task[],
  blockedOpen: Task[],
  gapCauses: GapCauses,
  contribution: ContribEntry[],
  goals: Goal[],
  recs: RebalancingRecommendation[],
  appliedRecs: Set<string>,
  weekLabel: string,
): string {
  const lines: string[] = [];
  lines.push(`**Weekly Review — ${weekLabel}**`);
  lines.push('');
  lines.push(`COMPLETED (${completedThisWeek.length} tasks):`);
  lines.push(fmtList(completedThisWeek.map((t) => {
    const day = t.completedAt ? new Date(t.completedAt).toLocaleDateString(undefined, { weekday: 'short' }) : '';
    const who = t.assigneeUserId ? ` [@${t.assigneeUserId.substring(0, 8)}]` : '';
    return `- ${t.title}${who}${day ? ` · ${day}` : ''}`;
  })));
  if (slippedThisWeek.length > 0) {
    lines.push('');
    lines.push(`SLIPPED — DUE THIS WEEK, NOT DONE (${slippedThisWeek.length}):`);
    lines.push(fmtList(slippedThisWeek.map(fmtTask)));
  }
  if (unresolvedOverdue.length > 0) {
    lines.push('');
    lines.push(`UNRESOLVED OVERDUE CARRYOVER (${unresolvedOverdue.length}):`);
    lines.push(fmtList(unresolvedOverdue.map(fmtTask)));
  }
  if (gapCauses.total > 0) {
    lines.push('');
    lines.push('CAUSES OF SLIPPAGE:');
    if (gapCauses.withBlocker > 0)       lines.push(`- ${gapCauses.withBlocker} had a stated blocker`);
    if (gapCauses.withReopen > 0)        lines.push(`- ${gapCauses.withReopen} were reopened`);
    if (gapCauses.withoutContext > 0)    lines.push(`- ${gapCauses.withoutContext} slipped with no context`);
    if (gapCauses.unestimatedSlipped > 0) lines.push(`- ${gapCauses.unestimatedSlipped} had no time estimate`);
    if (gapCauses.blockerSamples.length > 0) {
      lines.push('  Common blockers: ' + gapCauses.blockerSamples.join(' / '));
    }
  }
  if (contribution.length > 0) {
    lines.push('');
    lines.push('CONTRIBUTION:');
    for (const u of contribution.slice(0, 6)) {
      const burden: string[] = [];
      if (u.blockedCount > 0) burden.push(`${u.blockedCount}B`);
      if (u.overdueCount > 0) burden.push(`${u.overdueCount}O`);
      lines.push(`- ${u.userId}: ${u.completedThisWeek} done · ${u.openTaskCount} open${burden.length ? ` · ${burden.join(' ')}` : ''}`);
    }
  }
  lines.push('');
  lines.push('CARRYING INTO NEXT WEEK:');
  const behindGoals = goals.filter((g) => g.progress.trackingStatus === 'behind');
  if (behindGoals.length > 0) lines.push(`- Behind goals: ${behindGoals.map((g) => g.title).join(', ')}`);
  if (unresolvedOverdue.length > 0) lines.push(`- Overdue carryover: ${unresolvedOverdue.length} tasks`);
  if (blockedOpen.length > 0)       lines.push(`- Still blocked: ${blockedOpen.length} tasks`);
  const unresolvedRecs = recs.filter((r) => !appliedRecs.has(r.taskId)).length;
  if (unresolvedRecs > 0)           lines.push(`- Unresolved rebalancing: ${unresolvedRecs} suggestions`);
  lines.push('');
  lines.push('Please summarize: what worked this week, what slipped and why, what caused the slippage, and what should change next week. Format this as a concise manager recap suitable for a team review meeting.');
  return lines.join('\n');
}

// ============================================================
// Intervention helpers
// ============================================================

const INTERVENTION_TYPE_CONFIG: Record<InterventionType, { label: string; tagCls: string; borderCls: string }> = {
  focus:    { label: 'Focus',    tagCls: 'bg-yellow-100 text-yellow-800', borderCls: 'border-l-yellow-400' },
  review:   { label: 'Review',   tagCls: 'bg-blue-100 text-blue-800',    borderCls: 'border-l-blue-400'   },
  unblock:  { label: 'Unblock',  tagCls: 'bg-red-100 text-red-800',      borderCls: 'border-l-red-400'    },
  reassign: { label: 'Reassign', tagCls: 'bg-purple-100 text-purple-800', borderCls: 'border-l-purple-400' },
  estimate: { label: 'Estimate', tagCls: 'bg-gray-100 text-gray-700',    borderCls: 'border-l-gray-400'   },
  defer:    { label: 'Defer',    tagCls: 'bg-indigo-100 text-indigo-800', borderCls: 'border-l-indigo-400' },
};

function buildInterventionPrimer(interventions: Intervention[], weekLabel: string): string {
  const lines: string[] = [];
  lines.push(`**Intervention Plan — ${weekLabel}**`);
  lines.push('');
  lines.push(`${interventions.length} action${interventions.length > 1 ? 's have' : ' has'} been flagged as priorities this week:`);
  lines.push('');
  interventions.forEach((inv, i) => {
    lines.push(`${i + 1}. [${inv.type.toUpperCase()}] ${inv.title}`);
    lines.push(`   What: ${inv.description}`);
    lines.push(`   Why: ${inv.reason}`);
    lines.push(`   Action: ${inv.suggestedActionLabel}`);
    lines.push('');
  });
  lines.push('Please help me turn these into a concrete action plan. For each item, suggest the specific steps to take this week. Keep the response brief and actionable.');
  return lines.join('\n');
}

// ============================================================
// WeeklyPlanPage
// ============================================================

type PlanTab = 'today' | 'week' | 'review';

export default function WeeklyPlanPage() {
  const navigate = useNavigate();
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;

  const [tab, setTab] = useState<PlanTab>('today');
  const [appliedRecs, setAppliedRecs] = useState<Set<string>>(new Set());
  const [isStartingPlan, setIsStartingPlan] = useState(false);

  // ── Data ─────────────────────────────────────────────────
  const { data: goalsData, isLoading: loadingGoals } = useGetGoalsQuery(
    { workspaceId, status: 'active', activeOn: TODAY, limit: 10 },
    { skip: !workspaceId }
  );

  const { data: tasksResponse, isLoading: loadingTasks } = useGetTasksQuery(
    { workspaceId },
    { skip: !workspaceId }
  );

  const { data: mgmt, isLoading: loadingMgmt } = useGetManagementSummaryQuery(undefined, {
    skip: !workspaceId,
  });

  const { data: rebalancing, isLoading: loadingRebalance } =
    useGetTeamRebalancingRecommendationsQuery(undefined, { skip: !workspaceId });

  const [updateTask] = useUpdateTaskMutation();
  const [createConversation] = useCreateConversationMutation();
  const [addMessage] = useAddMessageMutation();

  // ── Shared derived ────────────────────────────────────────
  const tasks = tasksResponse?.tasks ?? [];

  const behindGoalIds = useMemo(
    () =>
      new Set(
        (goalsData?.goals ?? [])
          .filter((g) => g.progress.trackingStatus === 'behind')
          .map((g) => g.id)
      ),
    [goalsData]
  );

  const blockedTaskIdSet = useMemo(
    () => new Set(mgmt?.gaps.blockedTaskIds ?? []),
    [mgmt]
  );
  const overdueTaskIdSet = useMemo(
    () => new Set(mgmt?.gaps.overdueTaskIds ?? []),
    [mgmt]
  );

  // ── Weekly tab derived ────────────────────────────────────
  const overdueTasks = useMemo(
    () =>
      tasks
        .filter((t) => t.status !== 'done' && !!t.dueDate && t.dueDate.split('T')[0] < TODAY)
        .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))
        .slice(0, 5),
    [tasks]
  );

  const dueThisWeek = useMemo(
    () =>
      tasks
        .filter(
          (t) =>
            t.status !== 'done' &&
            !!t.dueDate &&
            t.dueDate.split('T')[0] >= TODAY &&
            t.dueDate.split('T')[0] <= WEEK.end
        )
        .sort((a, b) => {
          const pri = ['urgent', 'high', 'medium', 'low'];
          return pri.indexOf(a.priority) - pri.indexOf(b.priority);
        })
        .slice(0, 5),
    [tasks]
  );

  const focusTasks = useMemo(
    () =>
      tasks
        .filter(
          (t) =>
            t.status !== 'done' &&
            !!t.dueDate &&
            t.dueDate.split('T')[0] <= WEEK.end &&
            (t.priority === 'urgent' || (!!t.goalId && behindGoalIds.has(t.goalId)))
        )
        .slice(0, 5),
    [tasks, behindGoalIds]
  );

  const unestimatedCount = useMemo(
    () => tasks.filter((t) => t.status !== 'done' && !t.estimatedMinutes).length,
    [tasks]
  );

  const behindGoalsCount =
    (goalsData?.goals ?? []).filter((g) => g.progress.trackingStatus === 'behind').length;

  // ── Today tab derived ─────────────────────────────────────
  const dueToday = useMemo(
    () =>
      tasks
        .filter((t) => t.status !== 'done' && t.dueDate?.split('T')[0] === TODAY)
        .sort((a, b) => {
          const pri = ['urgent', 'high', 'medium', 'low'];
          return pri.indexOf(a.priority) - pri.indexOf(b.priority);
        })
        .slice(0, 8),
    [tasks]
  );

  const overdueCarryover = useMemo(
    () =>
      tasks
        .filter(
          (t) => t.status !== 'done' && !!t.dueDate && t.dueDate.split('T')[0] < TODAY
        )
        .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))
        .slice(0, 5),
    [tasks]
  );

  const urgentOpen = useMemo(
    () =>
      tasks
        .filter(
          (t) =>
            t.status !== 'done' &&
            t.priority === 'urgent' &&
            t.dueDate?.split('T')[0] !== TODAY
        )
        .slice(0, 5),
    [tasks]
  );

  const activeBlocked = useMemo(
    () => tasks.filter((t) => t.status === 'blocked').slice(0, 6),
    [tasks]
  );

  // Changes since yesterday
  const completedSinceYesterday = useMemo(
    () =>
      tasks
        .filter(
          (t) =>
            t.status === 'done' &&
            (t.completedAt ? t.completedAt >= YESTERDAY_ISO : t.updatedAt >= YESTERDAY_ISO)
        )
        .slice(0, 6),
    [tasks]
  );

  const newlyBlocked = useMemo(
    () =>
      tasks.filter((t) => t.status === 'blocked' && t.updatedAt >= YESTERDAY_ISO).slice(0, 5),
    [tasks]
  );

  const reopened = useMemo(
    () =>
      tasks
        .filter(
          (t) => t.status !== 'done' && !!t.reopenReason && t.updatedAt >= YESTERDAY_ISO
        )
        .slice(0, 5),
    [tasks]
  );

  const hasChanges =
    completedSinceYesterday.length > 0 || newlyBlocked.length > 0 || reopened.length > 0;

  // Help Needed (Today tab)
  const helpNeeded = useMemo(() => {
    const byUser = new Map<string, HelpNeededEntry>();
    const capacityMap = new Map(
      (mgmt?.capacity ?? []).map((c) => [
        c.userId,
        { openTaskCount: c.openTaskCount, estimatedMinutes: c.estimatedMinutes },
      ])
    );
    for (const t of tasks) {
      if (!t.assigneeUserId) continue;
      const uid = t.assigneeUserId;
      if (!byUser.has(uid)) {
        const cap = capacityMap.get(uid) ?? { openTaskCount: 0, estimatedMinutes: 0 };
        byUser.set(uid, { userId: uid, blockedCount: 0, overdueCount: 0, ...cap });
      }
      const entry = byUser.get(uid)!;
      if (blockedTaskIdSet.has(t.id)) entry.blockedCount++;
      if (overdueTaskIdSet.has(t.id)) entry.overdueCount++;
    }
    return Array.from(byUser.values())
      .filter((u) => u.blockedCount > 0 || u.overdueCount > 0)
      .sort((a, b) => b.blockedCount + b.overdueCount - (a.blockedCount + a.overdueCount))
      .slice(0, 5);
  }, [tasks, blockedTaskIdSet, overdueTaskIdSet, mgmt]);

  // ── Predictive risk signals (This Week tab) ───────────────
  type RiskItem = { key: string; label: string; detail?: string; sev: 'high' | 'med' };

  const upcomingRisks = useMemo((): RiskItem[] => {
    const risks: RiskItem[] = [];

    // 1. Goals at risk: behind + has overdue/blocked tasks or bad pace gap
    const goalsAtRisk = (goalsData?.goals ?? []).filter((g) => {
      if (g.progress.trackingStatus !== 'behind') return false;
      const exec = g.executionSummary;
      const badPace = g.progress.paceGap != null && g.progress.paceGap < -10;
      return exec != null && (exec.overdueTasks > 0 || exec.blockedTasks > 0 || badPace);
    });
    goalsAtRisk.slice(0, 2).forEach((g) => {
      const exec = g.executionSummary!;
      const reasons: string[] = [];
      if (g.progress.paceGap != null && g.progress.paceGap < -10)
        reasons.push(`${Math.abs(g.progress.paceGap).toFixed(0)}% behind pace`);
      if (exec.overdueTasks > 0)
        reasons.push(`${exec.overdueTasks} overdue task${exec.overdueTasks > 1 ? 's' : ''}`);
      if (exec.blockedTasks > 0)
        reasons.push(`${exec.blockedTasks} blocked`);
      risks.push({
        key: `goal-${g.id}`,
        label: `Goal "${g.title}" likely to miss this period`,
        detail: `Behind${reasons.length > 0 ? ` with ${reasons.join(' and ')}` : ''} — likely to miss without intervention`,
        sev: 'high',
      });
    });

    // 2. Tasks due within 48h not started or blocked
    const in48hStr = (() => { const d = new Date(); d.setHours(d.getHours() + 48); return d.toISOString().split('T')[0]; })();
    const atRisk48h = tasks.filter((t) => {
      if (!t.dueDate) return false;
      const d = t.dueDate.split('T')[0];
      return d >= TODAY && d <= in48hStr && (t.status === 'todo' || t.status === 'blocked');
    });
    if (atRisk48h.length > 0) {
      const blockedOf = atRisk48h.filter((t) => t.status === 'blocked').length;
      const notStartedOf = atRisk48h.length - blockedOf;
      const parts: string[] = [];
      if (notStartedOf > 0) parts.push(`${notStartedOf} not started`);
      if (blockedOf > 0) parts.push(`${blockedOf} blocked`);
      risks.push({
        key: 'tasks-48h',
        label: `${atRisk48h.length} task${atRisk48h.length > 1 ? 's' : ''} due within 48h — ${parts.join(', ')}`,
        sev: 'med',
      });
    }

    // 3. Reopened tasks still open — high risk of another slip
    const reopenedStillOpen = tasks.filter((t) => t.status !== 'done' && !!t.reopenReason);
    if (reopenedStillOpen.length > 0)
      risks.push({
        key: 'reopened',
        label: `${reopenedStillOpen.length} reopened task${reopenedStillOpen.length > 1 ? 's' : ''} still open — risk of repeat slip`,
        sev: 'med',
      });

    // 4. Team member at risk of overload — high current load + upcoming tasks due this week
    const upcomingByUser = new Map<string, number>();
    for (const t of tasks) {
      if (!t.assigneeUserId || t.status === 'done') continue;
      if (t.dueDate && t.dueDate.split('T')[0] >= TODAY && t.dueDate.split('T')[0] <= WEEK.end)
        upcomingByUser.set(t.assigneeUserId, (upcomingByUser.get(t.assigneeUserId) ?? 0) + 1);
    }
    const overloadRisk = (mgmt?.capacity ?? []).filter(
      (c) => c.openTaskCount > 7 && (upcomingByUser.get(c.userId) ?? 0) >= 3
    );
    if (overloadRisk.length > 0)
      risks.push({
        key: 'team-overload-risk',
        label: `${overloadRisk.length} team member${overloadRisk.length > 1 ? 's' : ''} at risk of overload`,
        detail: `High current load with multiple tasks due this week — consider redistributing`,
        sev: 'med',
      });

    return risks.slice(0, 5);
  }, [goalsData, tasks, mgmt]);

  // ── Intervention plan (This Week tab) ────────────────────
  const interventions = useMemo(
    () =>
      deriveInterventions({
        goals: goalsData?.goals ?? [],
        tasks,
        mgmt,
        rebalancing,
        weekStart: WEEK.start,
        weekEnd: WEEK.end,
      }),
    [goalsData, tasks, mgmt, rebalancing],
  );

  // Dedup: suppress predictive risk items already covered by a higher-tier intervention.
  const filteredRisks = useMemo(() => {
    const coverage = getInterventionCoverage(interventions);
    return suppressCovered(upcomingRisks, coverage);
  }, [interventions, upcomingRisks]);

  // ── Review tab derived ────────────────────────────────────

  // A. Completed this week
  // Use completedAt if present; fall back to updatedAt for tasks marked done within the week.
  const completedThisWeek = useMemo(
    () =>
      tasks
        .filter((t) => {
          if (t.status !== 'done') return false;
          const stamp = t.completedAt ?? t.updatedAt;
          const date = stamp.split('T')[0];
          return date >= WEEK.start && date <= WEEK.end;
        })
        .sort((a, b) => {
          const stampA = a.completedAt ?? a.updatedAt;
          const stampB = b.completedAt ?? b.updatedAt;
          return stampB.localeCompare(stampA); // most recent first
        })
        .slice(0, 10),
    [tasks]
  );

  // B. Slipped / Missed
  // Tasks due within this week (Mon–Sun) that are still not done
  const slippedThisWeek = useMemo(
    () =>
      tasks
        .filter(
          (t) =>
            t.status !== 'done' &&
            !!t.dueDate &&
            t.dueDate.split('T')[0] >= WEEK.start &&
            t.dueDate.split('T')[0] <= WEEK.end
        )
        .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))
        .slice(0, 8),
    [tasks]
  );

  // Pre-existing overdue still unresolved (due before this week)
  const unresolvedOverdue = useMemo(
    () =>
      tasks
        .filter(
          (t) =>
            t.status !== 'done' &&
            !!t.dueDate &&
            t.dueDate.split('T')[0] < WEEK.start
        )
        .sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? ''))
        .slice(0, 5),
    [tasks]
  );

  // Still-blocked tasks
  const blockedOpen = useMemo(
    () => tasks.filter((t) => t.status === 'blocked').slice(0, 6),
    [tasks]
  );

  // C. Gap causes
  // Among slipped + unresolved overdue, tally cause signals
  const gapCauses = useMemo(() => {
    const slipped = [...slippedThisWeek, ...unresolvedOverdue];
    let withBlocker = 0;
    let withReopen = 0;
    let withoutContext = 0;
    const blockerSamples: string[] = [];

    for (const t of slipped) {
      if (t.blockerReason) {
        withBlocker++;
        if (blockerSamples.length < 3) blockerSamples.push(t.blockerReason);
      } else if (t.reopenReason) {
        withReopen++;
      } else {
        withoutContext++;
      }
    }

    const unestimatedSlipped = slipped.filter((t) => !t.estimatedMinutes).length;

    return { withBlocker, withReopen, withoutContext, unestimatedSlipped, blockerSamples, total: slipped.length };
  }, [slippedThisWeek, unresolvedOverdue]);

  // D. Load / Contribution — per-user completed + open + burden this week
  const contribution = useMemo(() => {
    const byUser = new Map<string, ContribEntry>();

    const capacityMap = new Map(
      (mgmt?.capacity ?? []).map((c) => [
        c.userId,
        { openTaskCount: c.openTaskCount, estimatedMinutes: c.estimatedMinutes },
      ])
    );

    // Seed from all tasks with assignees
    for (const t of tasks) {
      if (!t.assigneeUserId) continue;
      const uid = t.assigneeUserId;
      if (!byUser.has(uid)) {
        const cap = capacityMap.get(uid) ?? { openTaskCount: 0, estimatedMinutes: 0 };
        byUser.set(uid, {
          userId: uid,
          completedThisWeek: 0,
          blockedCount: 0,
          overdueCount: 0,
          ...cap,
        });
      }
      const entry = byUser.get(uid)!;
      // completed this week
      if (t.status === 'done') {
        const stamp = t.completedAt ?? t.updatedAt;
        const date = stamp.split('T')[0];
        if (date >= WEEK.start && date <= WEEK.end) entry.completedThisWeek++;
      }
      if (blockedTaskIdSet.has(t.id)) entry.blockedCount++;
      if (overdueTaskIdSet.has(t.id)) entry.overdueCount++;
    }

    return Array.from(byUser.values())
      .filter((u) => u.completedThisWeek > 0 || u.openTaskCount > 0)
      .sort((a, b) => b.completedThisWeek - a.completedThisWeek)
      .slice(0, 8);
  }, [tasks, mgmt, blockedTaskIdSet, overdueTaskIdSet]);

  const isLoading = loadingGoals || loadingTasks || loadingMgmt || loadingRebalance;

  // ── Handlers ─────────────────────────────────────────────
  const handleApplyRebalance = async (taskId: string, toUserId: string) => {
    try {
      await updateTask({ id: taskId, assigneeUserId: toUserId }).unwrap();
      setAppliedRecs((prev) => new Set([...prev, taskId]));
    } catch {
      // silently ignore — non-critical
    }
  };

  const handleAskAI = async (title: string, primer?: string) => {
    if (!workspaceId) return;
    setIsStartingPlan(true);
    try {
      const result = await createConversation({ workspaceId, title, mode: 'chat' }).unwrap();
      const sessionId = result.session.id;
      if (primer) {
        try {
          await addMessage({ sessionId, content: primer }).unwrap();
        } catch {
          // context send failed — still navigate
        }
      }
      navigate(`/conversations/${sessionId}?assistant=true`);
    } catch {
      // silently ignore
    } finally {
      setIsStartingPlan(false);
    }
  };

  // ── Guards ───────────────────────────────────────────────
  if (!activeWorkspace) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace to view planning.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const goals = goalsData?.goals ?? [];
  const recs = rebalancing?.recommendations ?? [];
  const overloaded = rebalancing?.overloadedUsers ?? [];
  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });

  const aiCtaLabel = {
    today:  'Generate standup agenda',
    week:   'Generate weekly planning agenda',
    review: 'Generate weekly review summary',
  }[tab];

  const aiTitle = {
    today:  `Daily standup: ${todayLabel}`,
    week:   `Weekly plan: ${WEEK.label}`,
    review: `End-of-week review: ${WEEK.label}`,
  }[tab];

  const headerSubtitle = {
    today:  todayLabel,
    week:   WEEK.label,
    review: WEEK.label,
  }[tab];

  const headerTitle = {
    today:  'Daily Standup',
    week:   'Weekly Planning',
    review: 'End-of-Week Review',
  }[tab];

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="space-y-5 max-w-4xl mx-auto">

      {/* Header with tab strip */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{headerTitle}</h1>
            <p className="text-sm text-gray-500 mt-0.5">{headerSubtitle}</p>
          </div>
          <button
            onClick={() => {
              let primer: string | undefined;
              if (tab === 'today') {
                primer = buildStandupPrimer(dueToday, overdueCarryover, activeBlocked, helpNeeded, todayLabel);
              } else if (tab === 'week') {
                primer = buildWeeklyPlanPrimer(goals, focusTasks, overdueTasks, dueThisWeek, mgmt, recs, overloaded, behindGoalsCount, WEEK.label);
              } else if (tab === 'review') {
                primer = buildWeeklyReviewPrimer(completedThisWeek, slippedThisWeek, unresolvedOverdue, blockedOpen, gapCauses, contribution, goals, recs, appliedRecs, WEEK.label);
              }
              handleAskAI(aiTitle, primer);
            }}
            disabled={isStartingPlan}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {isStartingPlan ? 'Starting…' : aiCtaLabel}
          </button>
        </div>

        {/* Tab strip */}
        <div className="flex gap-1 mt-4 border-b border-gray-100">
          {([
            { key: 'today',  label: 'Today'     },
            { key: 'week',   label: 'This Week'  },
            { key: 'review', label: 'Review'     },
          ] as { key: PlanTab; label: string }[]).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors -mb-px border-b-2 ${
                tab === key
                  ? 'border-purple-600 text-purple-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ======================================================
          TODAY TAB
         ====================================================== */}
      {tab === 'today' && (
        <>
          {/* A. Today's Focus */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <SectionHeader title="Today's Focus" linkTo="/tasks" linkLabel="All tasks" />
            {dueToday.length === 0 && overdueCarryover.length === 0 &&
             urgentOpen.length === 0 && activeBlocked.length === 0 ? (
              <EmptyState message="Nothing urgent on the radar today." />
            ) : (
              <div className="space-y-4">
                {dueToday.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                      Due Today ({dueToday.length})
                    </p>
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 divide-y divide-blue-100">
                      {dueToday.map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}
                {activeBlocked.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">
                      Blocked ({activeBlocked.length})
                    </p>
                    <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 divide-y divide-amber-100">
                      {activeBlocked.map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}
                {overdueCarryover.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">
                      Overdue Carryover ({overdueCarryover.length}{overdueCarryover.length === 5 ? '+' : ''})
                    </p>
                    <div className="rounded-lg border border-red-100 bg-red-50 px-3 divide-y divide-red-100">
                      {overdueCarryover.map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}
                {urgentOpen.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-2">
                      Urgent — No Due Date ({urgentOpen.length})
                    </p>
                    <div className="rounded-lg border border-purple-100 bg-purple-50 px-3 divide-y divide-purple-100">
                      {urgentOpen.map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* B. Changes Since Yesterday */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <SectionHeader title="Since Yesterday" />
            <p className="text-xs text-gray-400 -mt-2 mb-3">
              Based on task updates in the last ~24 hours
            </p>
            {!hasChanges ? (
              <EmptyState message="No activity recorded since yesterday." />
            ) : (
              <div className="space-y-4">
                {completedSinceYesterday.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2">
                      Completed ({completedSinceYesterday.length})
                    </p>
                    <div className="space-y-1">
                      {completedSinceYesterday.map((t) => (
                        <div key={t.id} className="flex items-center gap-2 py-1.5">
                          <span className="text-green-500 text-sm flex-shrink-0">✓</span>
                          <p className="text-sm text-gray-600 truncate line-through">{t.title}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {newlyBlocked.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">
                      Newly Blocked ({newlyBlocked.length})
                    </p>
                    <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 divide-y divide-amber-100">
                      {newlyBlocked.map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}
                {reopened.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2">
                      Reopened ({reopened.length})
                    </p>
                    <div className="rounded-lg border border-orange-100 bg-orange-50 px-3 divide-y divide-orange-100">
                      {reopened.map((t) => (
                        <div key={t.id} className="py-2">
                          <TaskRow task={t} />
                          {t.reopenReason && (
                            <p className="text-xs text-orange-500 pl-4 mt-0.5 truncate">
                              Reason: {t.reopenReason}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* C. Help Needed */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <SectionHeader title="Help Needed" linkTo="/team" linkLabel="View team" />
            {helpNeeded.length === 0 ? (
              <EmptyState message="No team members are blocked or overdue right now." />
            ) : (
              <div className="space-y-2">
                {helpNeeded.map((u) => (
                  <div
                    key={u.userId}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600 flex-shrink-0">
                      {u.userId.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.userId}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {u.openTaskCount} open
                        {u.estimatedMinutes > 0 && ` · ~${Math.round(u.estimatedMinutes / 60)}h estimated`}
                      </p>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      {u.blockedCount > 0 && (
                        <Link
                          to="/tasks"
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium hover:bg-amber-200 transition-colors"
                        >
                          {u.blockedCount} blocked
                        </Link>
                      )}
                      {u.overdueCount > 0 && (
                        <Link
                          to="/tasks"
                          className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium hover:bg-red-200 transition-colors"
                        >
                          {u.overdueCount} overdue
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ======================================================
          THIS WEEK TAB
         ====================================================== */}
      {tab === 'week' && (
        <>
          {/* Risk Signals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {
                label: 'Behind goals',
                value: behindGoalsCount,
                color: behindGoalsCount > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white',
                textColor: behindGoalsCount > 0 ? 'text-red-700' : 'text-gray-400',
                link: '/goals',
              },
              {
                label: 'Overdue',
                value: mgmt?.gaps.overdueTasks ?? 0,
                color: (mgmt?.gaps.overdueTasks ?? 0) > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white',
                textColor: (mgmt?.gaps.overdueTasks ?? 0) > 0 ? 'text-red-700' : 'text-gray-400',
                link: '/tasks',
              },
              {
                label: 'Blocked',
                value: mgmt?.gaps.blockedTasks ?? 0,
                color: (mgmt?.gaps.blockedTasks ?? 0) > 0 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white',
                textColor: (mgmt?.gaps.blockedTasks ?? 0) > 0 ? 'text-amber-700' : 'text-gray-400',
                link: '/tasks',
              },
              {
                label: 'Unestimated',
                value: unestimatedCount,
                color: 'border-gray-200 bg-white',
                textColor: unestimatedCount > 0 ? 'text-gray-700' : 'text-gray-400',
                link: '/tasks',
              },
            ].map(({ label, value, color, textColor, link }) => (
              <Link
                key={label}
                to={link}
                className={`rounded-xl border p-4 shadow-sm flex flex-col items-center hover:shadow-md transition-shadow ${color}`}
              >
                <span className={`text-2xl font-bold ${textColor}`}>{value}</span>
                <span className="text-xs text-gray-500 mt-1 text-center">{label}</span>
              </Link>
            ))}
          </div>

          {/* Active Goals This Week */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <SectionHeader
              title="Active Goals This Week"
              count={goals.length}
              linkTo="/goals"
              linkLabel="Manage goals"
            />
            {goals.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">
                No active goals for this week.{' '}
                <Link to="/goals" className="text-blue-600 hover:underline">Add a goal →</Link>
              </p>
            ) : (
              <div className="space-y-2">
                {goals.map((goal) => {
                  const ts = goal.progress.trackingStatus;
                  const { label, cls } = TRACKING_BADGE[ts] ?? TRACKING_BADGE.no_data;
                  const exec = goal.executionSummary;
                  return (
                    <div
                      key={goal.id}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{goal.title}</p>
                        {exec && exec.overdueTasks > 0 && (
                          <p className="text-xs text-red-500 mt-0.5">
                            {exec.overdueTasks} overdue task{exec.overdueTasks !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                      {goal.progress.progressPercent != null && (
                        <span className="text-xs text-gray-500 flex-shrink-0 tabular-nums">
                          {goal.progress.progressPercent.toFixed(0)}%
                        </span>
                      )}
                      <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>
                        {label}
                      </span>
                      {exec && exec.linkedTasks > 0 && (
                        <Link
                          to={`/tasks?goalId=${goal.id}&gn=${encodeURIComponent(goal.title)}`}
                          className="flex-shrink-0 text-xs text-blue-600 hover:underline"
                        >
                          {exec.linkedTasks} task{exec.linkedTasks !== 1 ? 's' : ''}
                        </Link>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Due This Week */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <SectionHeader title="Due This Week" linkTo="/tasks" linkLabel="View all tasks" />
            {overdueTasks.length === 0 && dueThisWeek.length === 0 ? (
              <EmptyState message="No tasks due this week." />
            ) : (
              <div className="space-y-4">
                {focusTasks.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-2">
                      Priority Focus
                    </p>
                    <div className="rounded-lg border border-purple-100 bg-purple-50 px-3 divide-y divide-purple-100">
                      {focusTasks.map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}
                {overdueTasks.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">
                      Overdue Carryover ({overdueTasks.length}{overdueTasks.length === 5 ? '+' : ''})
                    </p>
                    <div className="rounded-lg border border-red-100 bg-red-50 px-3 divide-y divide-red-100">
                      {overdueTasks.map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}
                {dueThisWeek.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Due This Week ({dueThisWeek.length}{dueThisWeek.length === 5 ? '+' : ''})
                    </p>
                    <div className="rounded-lg border border-gray-100 px-3 divide-y divide-gray-100">
                      {dueThisWeek.map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Upcoming Risks — items already covered by interventions are suppressed */}
          {filteredRisks.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <SectionHeader title="Upcoming Risks" />
              <div className="space-y-2">
                {filteredRisks.map((risk) => (
                  <div
                    key={risk.key}
                    className={`flex items-start gap-3 p-3 rounded-lg ${
                      risk.sev === 'high'
                        ? 'bg-red-50 border border-red-100'
                        : 'bg-amber-50 border border-amber-100'
                    }`}
                  >
                    <span className={`flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full ${
                      risk.sev === 'high' ? 'bg-red-500' : 'bg-amber-500'
                    }`} />
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${
                        risk.sev === 'high' ? 'text-red-800' : 'text-amber-800'
                      }`}>
                        {risk.label}
                      </p>
                      {risk.detail && (
                        <p className="text-xs text-gray-500 mt-0.5">{risk.detail}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Predictive · Based on current task and goal data
              </p>
            </div>
          )}

          {/* Intervention Plan */}
          {interventions.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <SectionHeader title="Intervention Plan" count={interventions.length} />
              <div className="space-y-3">
                {interventions.map((inv) => {
                  const cfg = INTERVENTION_TYPE_CONFIG[inv.type];
                  return (
                    <div
                      key={inv.id}
                      className={`flex gap-3 p-3 rounded-lg border-l-4 bg-gray-50 ${cfg.borderCls}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${cfg.tagCls}`}>
                            {cfg.label}
                          </span>
                          {inv.severity === 'high' && (
                            <span className="text-xs text-red-600 font-medium">High priority</span>
                          )}
                        </div>
                        <p className="text-sm font-medium text-gray-900">{inv.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{inv.description}</p>
                        <p className="text-xs text-gray-400 italic mt-0.5">Why: {inv.reason}</p>
                      </div>
                      <button
                        onClick={() => inv.actionPath && navigate(inv.actionPath)}
                        className="flex-shrink-0 self-start text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline mt-0.5"
                      >
                        {inv.suggestedActionLabel} →
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-3 border-t border-gray-100">
                <button
                  onClick={() =>
                    handleAskAI(
                      'Intervention action plan',
                      buildInterventionPrimer(interventions, WEEK.label),
                    )
                  }
                  disabled={isStartingPlan}
                  className="w-full py-2 px-4 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-xs font-medium rounded-lg transition-all disabled:opacity-60"
                >
                  {isStartingPlan ? 'Opening AI...' : 'Ask AI to turn this into an action plan'}
                </button>
              </div>
            </div>
          )}

          {/* Team Load + Rebalancing */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <SectionHeader title="Team Load" linkTo="/team" linkLabel="View team" />
            {overloaded.length === 0 && recs.length === 0 ? (
              <EmptyState message="Team load is balanced. No rebalancing needed." />
            ) : (
              <div className="space-y-4">
                {overloaded.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">
                      Overloaded ({overloaded.length})
                    </p>
                    <div className="space-y-1.5">
                      {overloaded.slice(0, 3).map((u) => (
                        <div key={u.userId} className="flex items-center gap-3 text-sm">
                          <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center text-xs font-medium text-red-700 flex-shrink-0">
                            {u.userId.substring(0, 2).toUpperCase()}
                          </div>
                          <span className="text-gray-700 flex-1 truncate">{u.userId}</span>
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            {u.openTasks} open
                            {u.estimatedMinutes > 0 && ` · ~${Math.round(u.estimatedMinutes / 60)}h`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {recs.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">
                      Rebalancing Suggestions ({recs.length})
                    </p>
                    <div className="space-y-2">
                      {recs.slice(0, 3).map((rec) => {
                        const applied = appliedRecs.has(rec.taskId);
                        return (
                          <div
                            key={rec.taskId}
                            className={`flex items-start gap-3 p-3 rounded-lg border text-sm ${
                              applied ? 'bg-green-50 border-green-100' : 'bg-amber-50 border-amber-100'
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{rec.taskTitle}</p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {rec.fromUserId.substring(0, 8)} → {rec.toUserId.substring(0, 8)}
                                {' · '}{rec.reason}
                              </p>
                            </div>
                            {applied ? (
                              <span className="flex-shrink-0 text-xs text-green-600 font-medium pt-0.5">Reassigned ✓</span>
                            ) : (
                              <button
                                onClick={() => handleApplyRebalance(rec.taskId, rec.toUserId)}
                                className="flex-shrink-0 text-xs text-amber-700 hover:text-amber-900 font-medium pt-0.5 hover:underline"
                              >
                                Reassign →
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {(rebalancing?.underloadedUsers ?? []).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-2">
                      Available Capacity
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(rebalancing?.underloadedUsers ?? []).slice(0, 4).map((u) => (
                        <span
                          key={u.userId}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-100 rounded-lg text-xs text-green-700"
                        >
                          <span className="font-medium">{u.userId.substring(0, 8)}</span>
                          <span className="text-green-500">{u.openTasks} tasks</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* ======================================================
          REVIEW TAB — End-of-week manager recap
         ====================================================== */}
      {tab === 'review' && (
        <>
          {/* Summary scorecard */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              {
                label: 'Completed',
                value: mgmt?.progress.completedThisWeek ?? completedThisWeek.length,
                color: 'border-green-200 bg-green-50',
                textColor: 'text-green-700',
              },
              {
                label: 'Slipped',
                value: slippedThisWeek.length,
                color: slippedThisWeek.length > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white',
                textColor: slippedThisWeek.length > 0 ? 'text-red-700' : 'text-gray-400',
              },
              {
                label: 'Still Blocked',
                value: blockedOpen.length,
                color: blockedOpen.length > 0 ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-white',
                textColor: blockedOpen.length > 0 ? 'text-amber-700' : 'text-gray-400',
              },
              {
                label: 'Behind Goals',
                value: behindGoalsCount,
                color: behindGoalsCount > 0 ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white',
                textColor: behindGoalsCount > 0 ? 'text-red-700' : 'text-gray-400',
              },
            ].map(({ label, value, color, textColor }) => (
              <div
                key={label}
                className={`rounded-xl border p-4 shadow-sm flex flex-col items-center ${color}`}
              >
                <span className={`text-2xl font-bold ${textColor}`}>{value}</span>
                <span className="text-xs text-gray-500 mt-1 text-center">{label}</span>
              </div>
            ))}
          </div>

          {/* A. Completed This Week */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <SectionHeader
              title="Completed This Week"
              count={completedThisWeek.length}
              linkTo="/tasks"
              linkLabel="All tasks"
            />
            {completedThisWeek.length === 0 ? (
              <EmptyState message="No tasks were completed this week yet." />
            ) : (
              <div className="space-y-1">
                {completedThisWeek.map((t) => (
                  <div key={t.id} className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
                    <span className="text-green-500 text-sm flex-shrink-0">✓</span>
                    <p className="text-sm text-gray-700 flex-1 truncate">{t.title}</p>
                    {t.assigneeUserId && (
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {t.assigneeUserId.substring(0, 6)}
                      </span>
                    )}
                    {t.completedAt && (
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {new Date(t.completedAt).toLocaleDateString(undefined, { weekday: 'short' })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* B. Slipped / Missed */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <SectionHeader title="Slipped / Missed" linkTo="/tasks" linkLabel="View tasks" />
            {slippedThisWeek.length === 0 && unresolvedOverdue.length === 0 && blockedOpen.length === 0 ? (
              <EmptyState message="Nothing slipped this week. Great execution!" />
            ) : (
              <div className="space-y-4">
                {slippedThisWeek.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">
                      Due This Week — Not Done ({slippedThisWeek.length})
                    </p>
                    <div className="rounded-lg border border-red-100 bg-red-50 px-3 divide-y divide-red-100">
                      {slippedThisWeek.map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}
                {unresolvedOverdue.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2">
                      Unresolved Overdue Carryover ({unresolvedOverdue.length}{unresolvedOverdue.length === 5 ? '+' : ''})
                    </p>
                    <div className="rounded-lg border border-orange-100 bg-orange-50 px-3 divide-y divide-orange-100">
                      {unresolvedOverdue.map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}
                {blockedOpen.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">
                      Still Blocked ({blockedOpen.length})
                    </p>
                    <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 divide-y divide-amber-100">
                      {blockedOpen.map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* C. Gap Causes */}
          {gapCauses.total > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <SectionHeader title="What Caused the Slippage" />
              <div className="space-y-3">
                {/* Cause breakdown */}
                <div className="flex flex-wrap gap-2">
                  {gapCauses.withBlocker > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-100 rounded-lg text-xs">
                      <span className="font-semibold text-amber-700">{gapCauses.withBlocker}</span>
                      <span className="text-gray-600">had a stated blocker</span>
                    </span>
                  )}
                  {gapCauses.withReopen > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 border border-orange-100 rounded-lg text-xs">
                      <span className="font-semibold text-orange-700">{gapCauses.withReopen}</span>
                      <span className="text-gray-600">were reopened</span>
                    </span>
                  )}
                  {gapCauses.withoutContext > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs">
                      <span className="font-semibold text-gray-600">{gapCauses.withoutContext}</span>
                      <span className="text-gray-500">slipped with no context</span>
                    </span>
                  )}
                  {gapCauses.unestimatedSlipped > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs">
                      <span className="font-semibold text-gray-600">{gapCauses.unestimatedSlipped}</span>
                      <span className="text-gray-500">had no time estimate</span>
                    </span>
                  )}
                </div>
                {/* Sample blocker reasons */}
                {gapCauses.blockerSamples.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1.5">Common blocker reasons:</p>
                    <ul className="space-y-1">
                      {gapCauses.blockerSamples.map((reason, i) => (
                        <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                          <span className="text-amber-400 flex-shrink-0 mt-0.5">⚠</span>
                          <span className="truncate">{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {gapCauses.withoutContext > 0 && (
                  <p className="text-xs text-gray-400 italic">
                    Tip: Ask team to add blockerReason when a task slips — helps next-week planning.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* D. Load / Contribution */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <SectionHeader title="Contribution This Week" linkTo="/team" linkLabel="View team" />
            {contribution.length === 0 ? (
              <EmptyState message="No per-user contribution data available." />
            ) : (
              <div className="space-y-2">
                {contribution.map((u) => (
                  <div
                    key={u.userId}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-700 flex-shrink-0">
                      {u.userId.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{u.userId}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {u.openTaskCount} open
                        {u.estimatedMinutes > 0 && ` · ~${Math.round(u.estimatedMinutes / 60)}h remaining`}
                      </p>
                    </div>
                    {/* Completed this week — hero number */}
                    <div className="text-center flex-shrink-0">
                      <p className={`text-lg font-bold ${u.completedThisWeek > 0 ? 'text-green-600' : 'text-gray-300'}`}>
                        {u.completedThisWeek}
                      </p>
                      <p className="text-xs text-gray-400 leading-none">done</p>
                    </div>
                    {/* Burden badges */}
                    {(u.blockedCount > 0 || u.overdueCount > 0) && (
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        {u.blockedCount > 0 && (
                          <span className="text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded font-medium">
                            {u.blockedCount}B
                          </span>
                        )}
                        {u.overdueCount > 0 && (
                          <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">
                            {u.overdueCount}O
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <p className="text-xs text-gray-400 pt-1">B = blocked tasks · O = overdue tasks</p>
              </div>
            )}
          </div>

          {/* E. Next Week Risks */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <SectionHeader title="Carrying Into Next Week" />
            {blockedOpen.length === 0 && unresolvedOverdue.length === 0 &&
             behindGoalsCount === 0 && recs.filter((r) => !appliedRecs.has(r.taskId)).length === 0 ? (
              <EmptyState message="Clean slate heading into next week." />
            ) : (
              <div className="space-y-4">

                {/* Behind goals */}
                {behindGoalsCount > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-2">
                      Behind Goals ({behindGoalsCount})
                    </p>
                    <div className="space-y-1.5">
                      {(goalsData?.goals ?? [])
                        .filter((g) => g.progress.trackingStatus === 'behind')
                        .slice(0, 4)
                        .map((g) => (
                          <div key={g.id} className="flex items-center gap-2 p-2 rounded-lg bg-red-50 border border-red-100">
                            <span className="text-red-500 text-xs flex-shrink-0">↓</span>
                            <p className="text-sm text-gray-800 flex-1 truncate">{g.title}</p>
                            {g.progress.progressPercent != null && (
                              <span className="text-xs text-gray-500 flex-shrink-0">
                                {g.progress.progressPercent.toFixed(0)}%
                              </span>
                            )}
                            <Link
                              to={`/tasks?goalId=${g.id}&gn=${encodeURIComponent(g.title)}`}
                              className="text-xs text-blue-600 hover:underline flex-shrink-0"
                            >
                              Tasks →
                            </Link>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Overdue carryover */}
                {unresolvedOverdue.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2">
                      Overdue Carryover ({unresolvedOverdue.length}{unresolvedOverdue.length === 5 ? '+' : ''})
                    </p>
                    <div className="rounded-lg border border-orange-100 bg-orange-50 px-3 divide-y divide-orange-100">
                      {unresolvedOverdue.map((t) => <TaskRow key={t.id} task={t} dimmed />)}
                    </div>
                  </div>
                )}

                {/* Active blocked */}
                {blockedOpen.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">
                      Still Blocked ({blockedOpen.length})
                    </p>
                    <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 divide-y divide-amber-100">
                      {blockedOpen.slice(0, 4).map((t) => <TaskRow key={t.id} task={t} />)}
                    </div>
                  </div>
                )}

                {/* Unresolved rebalancing recs */}
                {recs.filter((r) => !appliedRecs.has(r.taskId)).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Unresolved Rebalancing Suggestions
                    </p>
                    <div className="space-y-2">
                      {recs
                        .filter((r) => !appliedRecs.has(r.taskId))
                        .slice(0, 3)
                        .map((rec) => (
                          <div
                            key={rec.taskId}
                            className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50 text-sm"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{rec.taskTitle}</p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {rec.fromUserId.substring(0, 8)} → {rec.toUserId.substring(0, 8)}
                                {' · '}{rec.reason}
                              </p>
                            </div>
                            <button
                              onClick={() => handleApplyRebalance(rec.taskId, rec.toUserId)}
                              className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-800 font-medium pt-0.5 hover:underline"
                            >
                              Reassign →
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

    </div>
  );
}
